import React, {useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import { TABLES } from '../../../../../Apis/Tables';
import TableFunction from '../../../../../components/table/TableFunction';
import { VIEW_TYPE } from '../../../../../enums/ViewType';
import { SimpleSearch } from '../../../../../reduxToolkit/Search/SearchApi';
import getTimezoneOffset from '../../../../../utils/GetTimeZone';
import GetDateFilter from '../../../Company/Payroll/Functionality/GetDateFilter';
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import AttendanceDayContainer from "../SubComponents/AttendanceDayContainer";
import {ATTENDANCE_MODES, MODEL_COLOR} from "../Models/Model";
import CustomImageTable from "../../../../../components/CustomImageTable";
import {IconAvatarTable} from "../../../../../components/buttons/IconButtons";
import CustomChip from "../../../../../components/CustomChip";
import {userType} from "../../../../../enums/userTypeEnum";
import {colors} from "../../../../../enums/colors";
import {EMPTY_VALUES} from "../../../../../Apis/functions";
import {DEFAULT_LEFT, DEFAULT_PAGINATION_START, SEARCH_MODEL} from '../../../../../components/table/Model/TableModel';
import CustomTooltip from "../../../../../components/CustomTooltip";

/**
 /**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @jobTittle: Full Stack Developer
 *
 */
const DiningAttendanceTableApp = ({
    setBodyForExport,
    finalArray,
    searchElement,
    selectedOption,
    setRender,
    color,
    modified,
    setModified
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.authenticatioauthennSlice);
    const userId = user?.data?.id;
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const tableId = "tbl_dat_attendance";
    const searchId = "tbl_dat_search_attendance";
    const { getAttendanceDate } = GetDateFilter();
    const headRef = useRef();
    const [attendanceStartDate, setAttendanceStartDate] = useState(getAttendanceDate(selectedOption).start);
    const [attendanceEndDate, setAttendanceEndDate] = useState( getAttendanceDate(selectedOption).end);
    const option = `${TABLES.FOODIE}`;
    const leftPosition = DEFAULT_LEFT;
    const firstTime = useRef(true);

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head w-190px">{t("name")}</CustomTh>,
            <CustomTh id="td_dat_second_column" tableId={tableId} headRef={headRef}/>,
        ]
    }

    const getTbody = () => {
        if (!results?.content?.length){
            setRender('');
            return { body: [], data:[] };
        }

        const body = results?.content.map((member, i) => {
            if(i === 0){
                setRender( <AttendanceDayContainer
                    color={color}
                    currentDays={member.days}
                    results={results?.content}
                    tableId={tableId}
                    headRef={headRef}
                    model={MODEL_COLOR}
                />);
            }

            const userTypeName = t(userType[member?.userTypeId]) || EMPTY_VALUES.DEFAULT;

            return [
                <CustomTd tableId={tableId} fixed={true} left={leftPosition} tooltip={true}  classes="h-90px first-column w-190px border-shades-200">
                    <CustomImageTable image={IconAvatarTable} containerClass={"d-flex align-items-center"}>
                        <div className={"ml-1"}>
                            <CustomTooltip
                                text={member?.fullName || member?.name}
                                classes={`mb-1 fs-14px text-ellipsis-2-rows`}
                                showIfTruncated={true}
                            />
                            <CustomChip legend={userTypeName}
                                        background={colors.secondary100}
                                        fontColor={colors.secondary700}
                                        borderColor={colors.secondary150}
                                        variant={'large'}
                            />
                        </div>
                    </CustomImageTable>
                </CustomTd>,
                <CustomTd tableId={tableId} classes={"h-90px"}/>,

            ]
        })

        return {
            body,
            data:results
        }
    }

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page, { type, searchElement }) => {
        const aux = type ?? selectedOption;

        // On mount with an active simple search, SearchSimple already triggers the request. Avoid duplicating it.
        if (firstTime.current) {
            firstTime.current = false;
            if (searchElement) return;
        }

        setRender('');
        setResults({ content: [], totalElements: 0 });
        const {start, end} = getAttendanceDate(aux);
        setAttendanceStartDate(start);
        setAttendanceEndDate(end);
        const timezone = getTimezoneOffset();

        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            attendanceTimeZone: timezone,
            attendanceStartDate: start,
            attendanceEndDate: end,
            attendanceSearchType: aux,
            key: searchElement,
            model: option
        });
        setBodyForExport({ ...body,  userId });
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                setResults(data);
            }
        });
    }

    const {thead, tbody, pagination, setPage, rowsPerPage } = TableFunction({
        data: results,
        getThead,
        getTbody,
        view: VIEW_TYPE.LIST.name,
        labelRowsPerPage: 'attendance_per_page',
        fixedPagination: false,
        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch:getData,//New - apply only with advanced search
        finalArray//New - apply only with advanced search
    });

    const handleChange = (e) => {
        const type = e.target.value;

        // Switching to daily unmounts this table; the daily table fetches on mount
        if(type === ATTENDANCE_MODES.daily) return;

        getData(DEFAULT_PAGINATION_START, { type, searchElement });
        setPage(DEFAULT_PAGINATION_START);
    }

    return {
        option,
        setPage,
        rowsPerPage,
        attendanceStartDate,
        attendanceEndDate,
        selectedOption,
        results,
        setResults,
        selectedDate,
        setSelectedDate,
        pagination,
        tableId,
        searchId,
        thead,
        tbody,
        handleChange,
        getData
    };
};

export default DiningAttendanceTableApp;
